import type { LeaderboardEntry, BlitzLeaderboardEntry, BlitzEndReason } from './types';

const STORAGE_KEY = 'set_game_top_scores_v1';

export function getLeaderboard(): LeaderboardEntry[] {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) return [];
    const entries: LeaderboardEntry[] = JSON.parse(data);
    return entries.sort((a, b) => a.timeMs - b.timeMs).slice(0, 5);
  } catch (e) {
    console.error('Failed to parse leaderboard', e);
    return [];
  }
}

export function saveScore(
  timeMs: number,
  hintsUsed: number
): { rank: number | null; leaderboard: LeaderboardEntry[] } {
  const penaltySeconds = hintsUsed * 15;
  const totalMs = timeMs + penaltySeconds * 1000;

  const current = getLeaderboard();
  const dateStr = new Date().toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const formattedTime = formatTimeMs(totalMs);

  const newEntry: LeaderboardEntry = {
    rank: 0,
    timeMs: totalMs,
    formattedTime,
    hintsUsed,
    penaltySeconds,
    date: dateStr,
  };

  const updated = [...current, newEntry].sort((a, b) => a.timeMs - b.timeMs);

  updated.forEach((entry, idx) => {
    entry.rank = idx + 1;
  });

  const top5 = updated.slice(0, 5);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(top5));
  } catch (e) {
    console.error('Failed to save score', e);
  }

  const foundIndex = top5.findIndex((e) => e === newEntry);
  const rank = foundIndex >= 0 ? foundIndex + 1 : null;

  return { rank, leaderboard: top5 };
}

export function clearLeaderboard(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    console.error('Failed to clear leaderboard', e);
  }
}

export function formatTimeMs(totalMs: number): string {
  const safeMs = Math.max(0, Math.floor(totalMs));
  const totalSeconds = Math.floor(safeMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

const BLITZ_STORAGE_KEY = 'set_game_blitz_scores_v1';

export function compareBlitzEntries(a: BlitzLeaderboardEntry, b: BlitzLeaderboardEntry): number {
  // 1. Sets found (higher is better)
  if (b.setsFound !== a.setsFound) {
    return b.setsFound - a.setsFound;
  }
  // 2. Status priority: cleared (3) > timeout (2) > mistake (1)
  const statusPriority: Record<BlitzEndReason, number> = {
    cleared: 3,
    timeout: 2,
    mistake: 1,
  };
  if (statusPriority[b.endReason] !== statusPriority[a.endReason]) {
    return statusPriority[b.endReason] - statusPriority[a.endReason];
  }
  // 3. For cleared runs: higher timeLeftMs is better
  if (a.endReason === 'cleared' && b.endReason === 'cleared') {
    return b.timeLeftMs - a.timeLeftMs;
  }
  return 0;
}

export function getBlitzLeaderboard(): BlitzLeaderboardEntry[] {
  try {
    const data = localStorage.getItem(BLITZ_STORAGE_KEY);
    if (!data) return [];
    const entries: BlitzLeaderboardEntry[] = JSON.parse(data);
    return entries.sort(compareBlitzEntries).slice(0, 5);
  } catch (e) {
    console.error('Failed to parse blitz leaderboard', e);
    return [];
  }
}

export function saveBlitzScore(
  setsFound: number,
  timeLeftMs: number,
  endReason: BlitzEndReason
): { rank: number | null; leaderboard: BlitzLeaderboardEntry[] } {
  const current = getBlitzLeaderboard();
  const dateStr = new Date().toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const formattedTimeLeft = formatTimeMs(timeLeftMs);
  const newEntry: BlitzLeaderboardEntry = {
    id: `${Date.now()}_${Math.random()}`,
    rank: 0,
    setsFound,
    timeLeftMs,
    formattedTimeLeft,
    endReason,
    date: dateStr,
  };

  const updated = [...current, newEntry].sort(compareBlitzEntries);

  updated.forEach((entry, idx) => {
    entry.rank = idx + 1;
  });

  const top5 = updated.slice(0, 5);
  try {
    localStorage.setItem(BLITZ_STORAGE_KEY, JSON.stringify(top5));
  } catch (e) {
    console.error('Failed to save blitz score', e);
  }

  const foundIndex = top5.findIndex((e) => e.id === newEntry.id);
  const rank = foundIndex >= 0 ? foundIndex + 1 : null;

  return { rank, leaderboard: top5 };
}

export function clearBlitzLeaderboard(): void {
  try {
    localStorage.removeItem(BLITZ_STORAGE_KEY);
  } catch (e) {
    console.error('Failed to clear blitz leaderboard', e);
  }
}


